import { useEffect, useMemo, useRef } from 'react'

import useToast from '@hooks/useToast.ts'

import type { ToastContent } from '@/lib/provider/ToastContext'

/**
 * Declarative toast component.
 *
 * Usage:
 * <ToastNotice
 *   id="update-available"
 *   open={hasUpdate}
 *   title="Update available"
 *   description="Reload to apply the latest version"
 *   variant="info"
 * />
 *
 * When `open` becomes true, the toast is shown (or replaced if it already exists).
 * When `open` becomes false or the component unmounts, the toast is closed.
 * Content prop changes while `open` stays true will re-show the toast (deduped by id in provider).
 */
interface ToastNoticeProps extends Omit<ToastContent, 'onClose'> {
    id: string
    open: boolean
    /**
     * Called when the toast is programmatically closed (e.g., user dismiss).
     * Note: For declarative control you should also update `open` state accordingly.
     */
    onOpenChange?: (open: boolean) => void
}

function ToastNotice(props: ToastNoticeProps) {

  const {
    open,
    id,
    onOpenChange,
    title,
    description,
    variant,
    icon,
    action,
    duration,
  } = props

  const { showToast, closeToast } = useToast()

  // This ref tracks the ID of the toast that is *currently* being managed by this component instance.
  const managedToastIdRef = useRef<string | null>(null)

  const payload = useMemo(
    () => ({
      id,
      title,
      description,
      variant,
      icon,
      action,
      duration,
      open,
      onClose: () => {
        // When the toast provider closes the toast (e.g., user click)
        // 1. Notify the parent to update its state.
        onOpenChange?.(false)
        // 2. Clear our internal ref so we know we're not managing a toast.
        managedToastIdRef.current = null
      },
    }),
    [
      id,
      title,
      description,
      variant,
      icon,
      action,
      duration,
      open,
      onOpenChange,
    ]
  )

  useEffect(() => {

    const { id, open } = payload

    if (open) {
      // If the ID has changed, the old toast is now stale. Close it.
      if (managedToastIdRef.current && managedToastIdRef.current !== id) {
        closeToast(managedToastIdRef.current)
      }

      // Show the new/updated toast and store its ID.
      showToast(payload)
      managedToastIdRef.current = id
    } else if (managedToastIdRef.current) {
      // `open` is false, so close the toast we are managing.
      closeToast(managedToastIdRef.current)
      managedToastIdRef.current = null
    }

    return () => {
      // If the component unmounts while a toast is still open,
      // we must clean it up.
      if (managedToastIdRef.current) {
        closeToast(managedToastIdRef.current)
        managedToastIdRef.current = null
      }
    }
  }, [payload, showToast, closeToast])

  return null
}

export default ToastNotice
